import { useEffect, useRef } from "react";

import { drawField, type FieldLook } from "../lib/pixelField";

interface Props {
  className?: string;
  /** Разные блоки на одном экране — разные поля. */
  seed?: number;
  /** Кадров в секунду: фону хватает меньше, чем брендовому блоку. */
  fps?: number;
  /** Характер поля; объект держать стабильным (константой модуля). */
  look?: FieldLook;
}

/**
 * Живая пиксельная матрица на фоне крупного блока.
 *
 * Цвет — CSS `color` элемента, прозрачность — CSS `opacity`: палитра живёт в
 * index.css, а не здесь. Вне экрана не рисует вовсе, при «уменьшить движение»
 * показывает один неподвижный кадр.
 */
export default function PixelField({ className = "", seed = 1, fps = 24, look }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let width = 0;
    let height = 0;
    let color = "";
    let raf = 0;
    let last = 0;

    const frame = (ms: number) =>
      drawField(ctx, width, height, still ? seed : ms / 1000, seed, color, look);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      color = getComputedStyle(canvas).color;
      frame(last);
    };

    const loop = (ms: number) => {
      raf = requestAnimationFrame(loop);
      if (ms - last < 1000 / fps) return;
      last = ms;
      frame(ms);
    };

    const sizes = new ResizeObserver(resize);
    sizes.observe(canvas);
    // Вне экрана кадры не нужны никому — процессор нужнее таблице подходов.
    const onScreen = new IntersectionObserver(([entry]) => {
      cancelAnimationFrame(raf);
      if (entry.isIntersecting && !still) raf = requestAnimationFrame(loop);
    });
    onScreen.observe(canvas);
    resize();

    return () => {
      cancelAnimationFrame(raf);
      sizes.disconnect();
      onScreen.disconnect();
    };
  }, [seed, fps, look]);

  return <canvas ref={ref} className={`pixel-field ${className}`} aria-hidden="true" />;
}
